import { DatePicker, QuantitySelector } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { RadioGroup, RadioGroupItem } from '@trellis/roots/radio-group'
import { Switch } from '@trellis/roots/switch'
import { Typography } from '@trellis/roots/typography'
import { EndConditionTypeSchema } from '@trellis/shared'
import type {
  EndConditionType,
  RecurrenceFrequency,
  RecurrenceRule,
} from '@trellis/shared'
import { Repeat } from 'lucide-react'

import { RecurrenceRuleBuilder } from './RecurrenceRuleBuilder'

export interface ScheduleSectionValue {
  enabled: boolean
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek: number[]
  dayOfMonth: number | undefined
  monthOfYear: number | undefined
  nextRunAt: string
  endConditionType: EndConditionType
  maxRuns: string
  endDate: string
  autoSend: boolean
}

interface ScheduleSectionProps {
  value: ScheduleSectionValue
  onChange: (value: ScheduleSectionValue) => void
  entityLabel: 'invoice' | 'expense'
  /** If true, toggle is always on and cannot be toggled off (edit-recurring mode). */
  locked?: boolean
  /** If true, entire section is hidden (edit-one-off mode). */
  hidden?: boolean
  children?: React.ReactNode
}

export function ScheduleSection({
  value,
  onChange,
  entityLabel,
  locked = false,
  hidden = false,
  children,
}: ScheduleSectionProps) {
  if (hidden) return null

  const isExpanded = value.enabled || locked

  function handleToggle(checked: boolean) {
    if (locked) return
    onChange({ ...value, enabled: checked })
  }

  function handleRecurrenceChange(
    update: Partial<
      Pick<
        RecurrenceRule,
        'frequency' | 'interval' | 'daysOfWeek' | 'dayOfMonth' | 'monthOfYear'
      >
    >,
  ) {
    onChange({
      ...value,
      ...(update.frequency !== undefined ? { frequency: update.frequency } : {}),
      ...(update.interval !== undefined ? { interval: update.interval } : {}),
      ...(update.daysOfWeek !== undefined ? { daysOfWeek: update.daysOfWeek } : {}),
      ...('dayOfMonth' in update ? { dayOfMonth: update.dayOfMonth } : {}),
      ...('monthOfYear' in update ? { monthOfYear: update.monthOfYear } : {}),
    })
  }

  return (
    <div className="bg-card rounded-xl border shadow-sm">
      <div className="flex items-center justify-between p-6 pb-4">
        <div>
          <div className="flex items-center gap-1.5">
            <Repeat className="size-4" aria-hidden="true" />
            <Typography variant="h5" as="h3">
              Recurrence
            </Typography>
          </div>
          <Typography variant="body-sm" color="muted" className="mt-1">
            How often this {entityLabel} is created automatically.
          </Typography>
        </div>
        {!locked && (
          <Switch
            checked={isExpanded}
            onCheckedChange={handleToggle}
            aria-label="Enable recurring schedule"
          />
        )}
      </div>

      {isExpanded && (
        <div className="space-y-4 border-t px-6 pt-4 pb-6">
          <Typography
            variant="label"
            color="muted"
            className="text-xs tracking-wider uppercase"
          >
            Schedule
          </Typography>

          <RecurrenceRuleBuilder
            frequency={value.frequency}
            interval={value.interval}
            daysOfWeek={value.daysOfWeek}
            startDate={value.nextRunAt}
            dayOfMonth={value.dayOfMonth}
            monthOfYear={value.monthOfYear}
            onChange={handleRecurrenceChange}
          />

          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <div className="space-y-2">
              <Label>Starts</Label>
              <DatePicker
                value={value.nextRunAt}
                onChange={(nextRunAt) => onChange({ ...value, nextRunAt })}
              />
            </div>

            <div className="space-y-2">
              <Label>Ends</Label>
              <div className="space-y-2">
                <RadioGroup
                  value={value.endConditionType}
                  onValueChange={(nextEndConditionType) =>
                    onChange({
                      ...value,
                      endConditionType:
                        nextEndConditionType as ScheduleSectionValue['endConditionType'],
                    })
                  }
                >
                  {EndConditionTypeSchema.options.map((condType) => (
                    <div key={condType} className="flex items-center gap-2">
                      <RadioGroupItem value={condType} id={`end-condition-${condType}`} />
                      <Label
                        htmlFor={`end-condition-${condType}`}
                        className="text-sm font-normal"
                      >
                        {condType === 'never'
                          ? 'Never'
                          : condType === 'after_n'
                            ? 'After N runs'
                            : 'On date'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                {value.endConditionType === 'after_n' && (
                  <QuantitySelector
                    min={1}
                    value={value.maxRuns === '' ? 1 : Number(value.maxRuns)}
                    onChange={(nextMaxRuns) =>
                      onChange({ ...value, maxRuns: String(nextMaxRuns) })
                    }
                    ariaLabel="Number of runs"
                    placeholder="Number of runs"
                    className="mt-1"
                  />
                )}
                {value.endConditionType === 'on_date' && (
                  <DatePicker
                    className="mt-1"
                    value={value.endDate}
                    onChange={(endDate) => onChange({ ...value, endDate })}
                  />
                )}
              </div>
            </div>
          </div>

          {children}
        </div>
      )}
    </div>
  )
}
